'use client'

import { useForm } from 'react-hook-form';
import { useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import PhoneInput, { formatPhoneNumber, formatPhoneNumberIntl, isValidPhoneNumber, isPossiblePhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { Modal } from 'react-bootstrap';
import Image from 'next/image';

import { useLanguage } from '@/app/api/LanguageContext';

import styles from './Forms.module.css';


const RECAPTCHA_PUBLIC_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_PUBLIC_KEY;
const CMS_HOST_URL_PUBLIC = process.env.NEXT_PUBLIC_CMS_HOST_URL;

const ContactForm = (props) => {

    const { title, subtitle, subject = 'Contacto Evanhub' } = props;

    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [messageClass, setMessageClass] = useState('');
    const { register, handleSubmit, setError, clearErrors, formState: { errors, isValid, isDirty }, setValue, reset } = useForm();
    const [showRecaptcha, setShowRecaptcha] = useState(true);
    const [recaptchaValue, setRecaptchaValue] = useState(null);
    const [recaptchaError, setRecaptchaError] = useState('');
    const [valuePhone, setValuePhone] = useState();
    const [isPhoneInputFocused, setIsPhoneInputFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [recaptchaKey, setRecaptchaKey] = useState(new Date().getTime());
    const [modalShow, setModalShow] = useState(false);


    const { locale } = useLanguage();

    const hasValueInitialState = {
        name: false,
        phone: false,
        email: false,
        emailValidate: false,
    };

    const [hasValue, setHasValue] = useState(hasValueInitialState);

    const resetHasValue = () => {
        setHasValue(hasValueInitialState);
    };

    const handleInputBlur = (fieldName, e) => {
        const value = e.target.value.trim();

        setValue(fieldName, value);

        setHasValue((prevValues) => ({
            ...prevValues,
            [fieldName]: value.length > 0,
        }));
    };


    const handlePhoneInputChange = (value) => {
        const phoneValue = value || undefined; // debe ser E.164 o undefined, no "undefined"
        setValuePhone(phoneValue);
        setIsPhoneInputFocused(Boolean(phoneValue));

        if (!phoneValue) {
            // Sin valor: no validar formato
            clearErrors('phone');
            return;
        }

        if (isValidPhoneNumber(phoneValue)) {
            clearErrors('phone');
        } else {
            setError('phone', { type: 'manual', message: locale === 'es' ? 'Número de teléfono inválido' : 'Invalid phone number' });
        }
    };

    const handlePhoneInputFocus = () => {
        setIsPhoneInputFocused(true);
    };

    const handlePhoneInputBlur = () => {
        setIsPhoneInputFocused(false);
    };

    /*const handleMouseEnter = () => {
        setIsHovered(true);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
    };*/

    const sendMail = async (data) => {
        try {
            return locale === 'es' ? 'Correo enviado exitosamente' : 'Form sent successfully!';
        } catch (error) {
            console.error('Error al enviar el correo:', error.message);
            throw new Error(locale === 'es' ? 'Error al enviar el correo' : 'Error sending email');
        }
    };

    const onSubmit = async (data) => {

        if (!recaptchaValue) {
            setRecaptchaError(locale === 'es' ? 'Por favor, verifica el reCAPTCHA' : 'Please verify the reCAPTCHA');
            return;
        } else {
            setRecaptchaError('');
        }

        setIsLoading(!isLoading);

        const resultMessage = await sendMail(data);
        setMessage(resultMessage);

        try {

            if (!recaptchaValue) {
                console.log('reCAPTCHA not validated. Form not submitted.');
                return;
            }

            /*const response = await fetch(CMS_HOST_URL_PUBLIC + '/api/consultas', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    data:
                    {
                        origin: subject ? subject : (locale === 'es' ? 'Contacto Evanhub' : 'Contact Evanhub'),
                        name: data.name,
                        email: data.email,
                        phone: valuePhone,
                        message: data.message,
                        'g-recaptcha-response': recaptchaValue,
                    }

                }),
            });*/

            try {
                // Enviar al endpoint interno que proxiea a Strapi con token del servidor
                const response = await fetch(`/api/consultas`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify({
                        data: {
                            origin: subject ? subject : (locale === 'es' ? 'Contacto Evanhub' : 'Contact Evanhub'),
                            name: data.name,
                            email: data.email,
                            phone: valuePhone,
                            message: data.message,
                            'g-recaptcha-response': recaptchaValue,
                        }
                    }),
                });

                // Lee cuerpo genérico y luego intenta parsear JSON
                const rawBody = await response.text();
                let parsedBody = null;
                try {
                    parsedBody = rawBody ? JSON.parse(rawBody) : null;
                } catch (_) {
                    parsedBody = null;
                }

                if (!response.ok) {
                    const serverMsg = parsedBody?.error?.message || parsedBody?.message || response.statusText || rawBody || (locale === 'es' ? 'Error desconocido' : 'Unknown error');
                    const bodyForLog = typeof parsedBody === 'object' && parsedBody !== null ? JSON.stringify(parsedBody) : rawBody;
                    console.error('Error al enviar:', 'status:', response.status, 'statusText:', response.statusText, 'body:', bodyForLog);
                    throw new Error(serverMsg);
                }

                console.log('Enviado correctamente:', parsedBody ?? rawBody);

                if (response.ok) {

                    //setMessage(locale === 'es' ? 'Consulta enviada con éxito' : 'Form sent successfully!');
                    if (typeof window !== 'undefined' && window.dataLayer && typeof window.dataLayer.push === 'function') {
                        window.dataLayer.push({
                            event: 'contactFormSubmitted',
                            formData: {
                                name: data.name,
                                email: data.email,
                                phone: valuePhone,
                                message: data.message
                            }
                        });
                    }

                    setMessageClass(styles.successMessage);

                    setModalShow(true);

                    await delay(5000);
                    setModalShow(false);
                    reset();
                    setValuePhone(undefined);
                    setIsPhoneInputFocused(false);
                    setRecaptchaKey(new Date().getTime());
                    resetHasValue();

                    //return '¡Formulario enviado con éxito!';
                } else {
                    throw new Error(locale === 'es' ? 'Error al enviar el formulario' : 'Error sending form');
                }

            } catch (err) {
                console.error('Excepción en fetch:', err);
            }


            /* console.log('Response Data:', response);
            if (!response.ok) {
                throw new Error('Error al enviar la consulta');
            }

            const responseData = await response.json(); */
            //console.log('Response Data:', responseData);

            setIsLoading(false);

        } catch (error) {

            console.error('Error:', error.message);
            if (error.response) {
                // Si hay una respuesta del servidor, imprime detalles específicos.
                console.error('Server Response:', error.response.data);
            }

            setIsLoading(false);

            setMessageClass(styles.errorMessage);

            await delay(1000);
            setMessageClass((prevClasses) => `${prevClasses} ${styles.fadeIn}`);

            await delay(3000);
            setMessageClass((prevClasses) => `${prevClasses} ${styles.fadeOut}`);

            await delay(500);
            setMessage('');
            setMessageClass('');

            return locale === 'es' ? '¡Error al enviar el formulario! Por favor, inténtalo de nuevo.' : 'Error sending form! Please try again.';
        }
    };

    function delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }


    function SuccessModal(props) {
        return (
            <Modal
                {...props}
                size="lg"
                aria-labelledby="contained-modal-title-vcenter"
                centered
                id="modalSuccess"
            >
                <Modal.Header closeButton></Modal.Header>
                <Modal.Body>
                    <Image src="/Evanhub-isotipo-degrade.svg" alt="Evanhub" width={115} height={115} quality={100} unoptimized={true} className="mx-auto d-block" />
                    <h4 className='h1 display-small text-center'>{locale === 'es' ? 'Consulta enviada' : 'Form sent'}</h4>
                    <p className='text-center'> {locale === 'es' ? 'Revisaremos tu solicitud y nos contactaremos contigo a la brevedad posible.' : 'We will review your request and contact you as soon as possible.'}</p>
                </Modal.Body>
            </Modal>
        );
    }

    return (
        <>
            {title && <h2 className='text-center display-large color-01 secondary-font mb-xl'>{title}</h2>}
            {subtitle && <p className={`${styles.Subtitle} text-center mb-xl`}>{subtitle}</p>}

            <div className="rendered-form">
                <form id="contactos" className={styles.formEvanhub} onSubmit={handleSubmit(onSubmit)} method="post" noValidate>
                    <div className={styles.innerForm}>
                        <input type="hidden" className="form-control" name="origin" value={subject} id="origin" />

                        <div className={`row ${styles.Row}`}>
                            <div className="col-sm-12 col-md-6 position-relative">
                                <input
                                    id="name"
                                    type="text"

                                    {...register('name', {
                                        required: locale === 'es' ? 'Este campo es obligatorio' : 'This field is required',
                                        maxLength: { value: 20, message: locale === 'es' ? 'Máximo 20 caracteres' : 'Maximum 20 characters' },
                                        minLength: { value: 3, message: locale === 'es' ? 'Mínimo 3 caracteres' : 'Minimum 3 characters' }
                                    })}
                                    onBlur={(e) => handleInputBlur('name', e)}
                                    className={`${styles.input} ${hasValue.name ? styles.isValidate : ''}`}
                                />
                                <label htmlFor="name">{locale == 'es' ? 'Nombre y apellido' : 'First name / Last name'} <span>*</span></label>
                                {errors.name && <span className={styles.inputError}>{errors.name.message}</span>}
                            </div>
                            <div className="col-sm-12 col-md-6 position-relative">
                                <PhoneInput
                                    id="phone"
                                    name="phone"
                                    className={styles.input_flag}
                                    defaultCountry="CL"
                                    value={valuePhone}
                                    onChange={handlePhoneInputChange}
                                    onFocus={handlePhoneInputFocus}
                                    onBlur={handlePhoneInputBlur}
                                //onMouseEnter={handleMouseEnter}
                                //onMouseLeave={handleMouseLeave}
                                />
                                <label htmlFor="phone" className={`${isPhoneInputFocused || valuePhone || isHovered ? 'focused' : ''} ${valuePhone ? 'active' : ''} ${styles.phone_label}`}>
                                    {locale == 'es' ? 'Teléfono' : 'Phone'} <span>*</span>
                                </label>
                                {errors.phone && <span className={styles.inputError}>{errors.phone.message}</span>}
                            </div>

                        </div>
                        <div className={`row ${styles.Row}`}>
                            <div className="col-sm-12 col-md-6 position-relative">
                                <input
                                    id="email"
                                    type="email"
                                    name="email"
                                    {...register('email', {
                                        required: locale === 'es' ? 'Este campo es obligatorio' : 'This field is required',
                                        pattern: { value: /^\S+@\S+$/i, message: locale === 'es' ? 'Correo electrónico inválido' : 'Invalid email' }
                                    })}
                                    onBlur={(e) => handleInputBlur('email', e)}
                                    className={`${styles.input} ${hasValue.email ? styles.isValidate : ''}`}
                                />
                                <label htmlFor="email">{locale == 'es' ? 'Correo electrónico' : 'Email'} <span>*</span></label>
                                {errors.email && <span className={styles.inputError}>{errors.email.message}</span>}
                            </div>
                            <div className="col-sm-12 col-md-6 position-relative">
                                <input
                                    id="emailValidate"
                                    type="email"
                                    name="emailValidate"
                                    {...register('emailValidate', {
                                        required: locale === 'es' ? 'Este campo es obligatorio' : 'This field is required',
                                        pattern: { value: /^\S+@\S+$/i, message: locale === 'es' ? 'Correo electrónico inválido' : 'Invalid email' },
                                    })}
                                    onBlur={(e) => handleInputBlur('emailValidate', e)}
                                    className={`${styles.input} ${hasValue.emailValidate ? styles.isValidate : ''}`}
                                />
                                <label htmlFor="emailValidate">{locale == 'es' ? 'Confirma tu correo electrónico' : 'Confirm your email'} <span>*</span></label>
                                {errors.emailValidate && <span className={styles.inputError}>{errors.emailValidate.message}</span>}
                            </div>

                        </div>
                        <div className={`row ${styles.Row}`}>
                            <div className="col-sm-12 col-md-12 position-relative">
                                <textarea
                                    type="text"
                                    className="form-control"
                                    name="message"
                                    placeholder={locale === 'es' ? "Escriba aquí su mensaje" : "Write your message here"}
                                    {...register('message')}
                                />
                            </div>
                        </div>
                        {showRecaptcha && (
                            <div className="formbuilder-button form-group field-submit mt-xl">
                                <ReCAPTCHA
                                    key={recaptchaKey}
                                    sitekey={RECAPTCHA_PUBLIC_KEY}
                                    onChange={(value) => {
                                        setRecaptchaValue(value);
                                        setRecaptchaError(''); // Elimina el mensaje de error si ya fue marcado
                                    }}
                                />
                                {recaptchaError && <span className={styles.inputError}>{recaptchaError}</span>}
                            </div>
                        )}
                        <div className={`row mt-xl ${styles.Row}`}>
                            <div className="col-sm-12 col-md-12 position-relative">
                                <button className="evanhub-btn btn-full__primary" type="submit" disabled={isLoading}>
                                    {isLoading ? <span className={styles.loader}></span> : locale === 'es' ? 'Enviar solicitud' : 'Send'}
                                </button>
                            </div>
                        </div>
                        <div className={`${styles.messageForm} ${messageClass}`}>
                            <p className="mb-0"> {message && message}</p>
                        </div>
                    </div>
                </form>
            </div>


            {/* Modal de éxito */}
            <SuccessModal
                show={modalShow}
                onHide={() => setModalShow(false)}
            />
            {/* Modal de éxito */}

        </>
    );
};

export default ContactForm;
